import {
    AutocompleteInput,
    BooleanInput,
    Breadcrumb,
    DateInput,
    Edit,
    NumberInput,
    ReferenceInput,
    SimpleForm,
    TextInput,
} from "@/components/admin";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FileDown, FileText, Landmark, Truck } from "lucide-react";
import { useRecordContext } from 'ra-core';
import { useWatch } from 'react-hook-form';
import { Link } from "react-router-dom";
import { DocumentItemsTable } from '../shared/DocumentItemsTable';
import { GrandTotalsWatcher } from '../shared/GrandTotalsWatcher';
import { RevisedReferenceWatcher } from '../shared/RevisedReferenceWatcher';
import { StatusSelectInput } from '../shared/StatusSelectInput';
import { TermsTemplateSelector } from '../shared/TermsTemplateSelector';
import { requireNonZeroTotal } from '../shared/validators';
import { FilteredContactInput } from '../../misc/FilteredContactInput';

/** Inner component — must live inside SimpleForm so useWatch binds to the form context */
const LogisticsFields = () => {
    const includeLogistics = useWatch({ name: 'include_logistics' });
    if (!includeLogistics) return null;
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 pt-4">
            <TextInput source="lr_number" label="LR Number" className="w-full" />
            <TextInput source="transporter_name" label="Transporter Name" className="w-full" />
            <NumberInput source="lr_amount" label="Freight (LR) Amount" className="w-full" />
            <TextInput source="delivery_terms" label="Delivery Terms" className="w-full" />
            <TextInput source="dispatch_through" label="Dispatch Through" className="w-full" />
            <TextInput source="destination" label="Destination" className="w-full" />
        </div>
    );
};

const PIEditTitle = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <span className="text-xl font-bold tracking-tight">Edit Proforma Invoice {record.reference}</span>
    );
};

const PIEditBreadcrumb = () => {
    const record = useRecordContext();
    if (!record) return null;
    return (
        <Breadcrumb>
            <Breadcrumb.Item><Link to="/proforma_invoices">Proforma Invoices</Link></Breadcrumb.Item>
            <Breadcrumb.Item><Link to={`/proforma_invoices/${record.id}/show`}>{record.reference}</Link></Breadcrumb.Item>
            <Breadcrumb.PageItem>Edit</Breadcrumb.PageItem>
        </Breadcrumb>
    );
};

export const PIEdit = () => (
    <Edit disableBreadcrumb title={<PIEditTitle />} mutationMode="pessimistic">
        <PIEditBreadcrumb />
        <SimpleForm className="max-w-5xl mx-auto space-y-6 relative" validate={requireNonZeroTotal}>

            {/* ── PI DETAILS ── */}
            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b bg-muted/50">
                    <div className="flex items-center gap-2">
                        <FileDown className="w-5 h-5 text-primary" />
                        <CardTitle className="text-lg font-semibold tracking-tight">Proforma Invoice Details</CardTitle>
                    </div>
                </CardHeader>
                <CardContent className="pt-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <TextInput
                            source="reference"
                            label="Proforma Invoice No."
                            disabled
                            className="w-full opacity-70"
                            helperText={false}
                        />
                        <StatusSelectInput
                            source="status"
                            className="w-full"
                            choices={[
                                { id: 'draft', name: 'Draft' },
                                { id: 'sent', name: 'Sent' },
                                { id: 'pending_approval', name: 'Pending Approval' },
                                { id: 'approved', name: 'Approved' },
                                { id: 'rejected', name: 'Rejected' },
                                { id: 'converted', name: 'Converted to Invoice' },
                                { id: 'revised', name: 'Revised' },
                            ]}
                        />
                        <DateInput source="date" className="w-full" />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                        <ReferenceInput source="quotation_id" reference="quotations">
                            <AutocompleteInput label="Related Quotation" className="w-full" />
                        </ReferenceInput>

                        <ReferenceInput source="company_id" reference="companies">
                            <AutocompleteInput label="Company" className="w-full" />
                        </ReferenceInput>
                        <FilteredContactInput source="customer_id" label="Customer Contact" className="w-full" />
                    </div>
                    {/* PI-1: editable Bill To / Ship To */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
                        <TextInput source="billing_address" label="Billing Address (Bill To)" multiline rows={3} className="w-full" />
                        <TextInput source="shipping_address" label="Shipping Address (Ship To)" multiline rows={3} className="w-full" />
                    </div>
                </CardContent>
            </Card>

            {/* ── LOGISTICS — opt-in via checkbox ── */}
            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b">
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                            <Truck className="w-5 h-5 text-muted-foreground" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Logistics (LR Details)</CardTitle>
                        </div>
                        {/*
                          BooleanInput saves true/false into `include_logistics`.
                          When false, LogisticsFields renders nothing, and lr_number /
                          transporter_name / lr_amount stay empty — so the preview
                          (PIShow) and the PDF (PIPDFTemplate) will not show the section.
                        */}
                        <BooleanInput
                            source="include_logistics"
                            label="Include Logistics"
                            helperText={false}
                        />
                    </div>
                </CardHeader>
                <CardContent>
                    <LogisticsFields />
                </CardContent>
            </Card>

            {/* ── LINE ITEMS & FINANCIALS ── */}
            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b">
                    <div className="flex items-center gap-2">
                        <Landmark className="w-5 h-5 text-primary" />
                        <CardTitle className="text-lg font-semibold tracking-tight">Line Items &amp; Financials</CardTitle>
                    </div>
                </CardHeader>
                <CardContent className="pt-6 space-y-6">
                    <DocumentItemsTable showSubItems />
                    <GrandTotalsWatcher />
                    <RevisedReferenceWatcher />

                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 bg-muted p-4 rounded-lg border">
                        <NumberInput source="cgst_amount" label="CGST (₹)" readOnly className="w-full" />
                        <NumberInput source="sgst_amount" label="SGST (₹)" readOnly className="w-full" />
                        <NumberInput source="igst_amount" label="IGST (₹)" readOnly className="w-full" />
                        <NumberInput source="total_amount" label="Grand Total (₹)" readOnly className="w-full font-bold text-primary" />
                        <div className="md:col-span-4">
                            <TextInput source="amount_in_words" label="Amount in Words" className="w-full italic" readOnly />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* ── TERMS & NOTES ── */}
            <Card className="w-full shadow-sm">
                <CardHeader className="pb-3 border-b bg-muted/50">
                    <div className="flex items-center justify-between w-full">
                        <div className="flex items-center gap-2">
                            <FileText className="w-5 h-5 text-primary" />
                            <CardTitle className="text-lg font-semibold tracking-tight">Terms &amp; Notes</CardTitle>
                        </div>
                        <TermsTemplateSelector type="quotation" />
                    </div>
                </CardHeader>
                <CardContent className="pt-6">
                    <TextInput
                        source="terms_conditions"
                        label="Terms & Conditions"
                        multiline
                        rows={4}
                        className="w-full max-w-none"
                    />
                </CardContent>
            </Card>
        </SimpleForm>
    </Edit>
);
